/* ---------------------------------------------------------------------------
   Where the network is: a map, then the route schematic.

   THE MAP came second, and only once there was something to put on it. This
   screen carried a note explaining that it was deliberately not a map, because
   one record held coordinates and a geographic plot would have been one pin and
   four apologies; all five now carry a position (see the COORDINATES note in
   src/data/seed.ts) and the note is gone with the reason for it. It is
   OpenStreetMap tiles with a bundled outline behind them — src/map/nepalMap.ts.

   THE STRIPS stayed, and are still underneath rather than replaced. Each carrying
   road is drawn as a strip with its structures placed by chainage, which is the
   location every record actually has as a matter of record: the map's pins are
   map pins, and an engineer driving the road navigates by chainage. The two
   answer different questions — "whereabouts in the country" and "which bridges
   are on this road, in what order, how far apart" — and the second is still most
   of what anyone opens this screen for.
   --------------------------------------------------------------------------- */

import { h, ICONS } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { barRow, button, card, cardHead, fact, meter, tag } from '../components/widgets.ts';
import { BANDS, bandCounts, bandLabel, screeningGaps } from '../data/screening.ts';
import { panel } from '../components/panel.ts';
import { mapRefusal } from '../components/tabbar.ts';
import { nepalMapCard, selectOnMap } from '../map/nepalMap.ts';
import {
  allBridges,
  districtRollups,
  inspectionStanding,
  standingLabel,
  routeStrips,
  shortName,
  withInventorySheet,
  type RouteStop,
  type RouteStrip,
  geometryCaption,
} from '../data/derive.ts';
import type { BridgeView } from '../data/types.ts';
import { join, NOT_RECORDED } from '../data/recorded.ts';
import { stationOf } from '../tenant/format.ts';
import { agency, vocab } from '../tenant/agency.ts';
import { active } from '../tenant/registry.ts';

/* --------------------------- route cards --------------------------- */

/*
   One card per road: a short strip on top as the overview, and under it the
   structures as a list in chainage order. The strip used to carry every label
   itself, fanned out over up to eight rows above and below the axis, and on
   DPWH's 52 structures between km 1246 and 1377 in a 400 px column that left
   most names cut to "B…". The strip now draws dots only — position, deck length
   by size, a twin by a double ring — and the names live in the list, which has
   room for all of them.

   A road drawn as several stretches (STRIP_BREAK_KM in src/data/derive.ts) is
   still one card, with a switch between its stretches, rather than two cards
   both titled "Maharlika Highway".
*/

/** One stop in the list and one section heading. Paired with `.route__stop` and `.route__sec`. */
const STOP_H = 34;
const SEC_H = 32;
/** The list's top padding. Paired with `.route__list`. */
const LIST_PAD = 4;
/** Tallest the list grows before it scrolls. Paired with `.route__list`. */
const LIST_MAX = 360;

/** How far each list has been scrolled, so a repaint draws the window where it is. */
const scrolled = new Map<string, { top: number; height: number }>();

/** The roads to draw, each with its stretches in chainage order. */
interface RoadCard {
  key: string;
  stretches: RouteStrip[];
}

function roadCards(strips: RouteStrip[]): RoadCard[] {
  const byRoad = new Map<string, RouteStrip[]>();
  for (const strip of strips) {
    const key = strip.route ?? strip.roadName;
    byRoad.set(key, [...(byRoad.get(key) ?? []), strip]);
  }
  return Array.from(byRoad, ([key, stretches]) => ({
    key,
    stretches: [...stretches].sort((a, b) => a.from - b.from),
  }));
}

/** The busiest roads first, at most `MAX_STRIPS` of them. */
function roadsShown(all: RoadCard[]): RoadCard[] {
  const size = (r: RoadCard) => r.stretches.reduce((n, st) => n + st.count, 0);
  return all.length > MAX_STRIPS ? [...all].sort((a, b) => size(b) - size(a)).slice(0, MAX_STRIPS) : all;
}

/**
 * Which stretch a card shows: the one picked, until the selection moves to a
 * structure on another stretch of the road — then that one. Otherwise the
 * stretch holding the selection, otherwise the first.
 */
function stretchShown(s: Readonly<State>, road: RoadCard): RouteStrip {
  const holding = road.stretches.find((st) => st.markers.some((m) => m.bridge.id === s.selectedId));
  const pick = s.routeStretch[road.key];
  if (pick && (pick.forId === s.selectedId || !holding)) {
    const picked = road.stretches.find((st) => st.from === pick.from);
    if (picked) return picked;
  }
  return holding ?? road.stretches[0]!;
}

/** "km 1246–1270", or one kilometre where both ends fall in it. */
function kmSpan(from: number, to: number): string {
  const a = Math.floor(from);
  const b = Math.floor(to);
  return a === b ? `km ${a}` : `km ${a}–${b}`;
}

/** A stop's dot, in px: by its longest deck, square-rooted so 240 m does not swamp 15 m. */
function dotSize(stop: RouteStop): number {
  const lengths = stop.bridges.map((b) => b.length).filter((l): l is number => l != null);
  return lengths.length ? Math.round(6 + Math.sqrt(Math.max(...lengths)) * 0.6) : 6;
}

/** Where each stop sits in the list, top and bottom in px, as the CSS draws it. */
function stopOffsets(strip: RouteStrip): { top: number; bottom: number }[] {
  const out: { top: number; bottom: number }[] = [];
  let y = LIST_PAD;
  const groups = strip.sections.length ? strip.sections.map((sec) => sec.stops) : [strip.stops];
  for (const stops of groups) {
    if (strip.sections.length) y += SEC_H;
    for (let i = 0; i < stops.length; i++, y += STOP_H) out.push({ top: y, bottom: y + STOP_H });
  }
  return out;
}

/**
 * The stretch of the strip the list is showing, as left and width percentages —
 * or null when the whole list is in view and a window would mark everything.
 */
function listWindow(strip: RouteStrip, top: number, height: number): { left: string; width: string } | null {
  const offsets = stopOffsets(strip);
  const end = (offsets[offsets.length - 1]?.bottom ?? 0) + LIST_PAD;
  if (end <= height + 1) return null;
  const seen = offsets.flatMap((o, i) => (o.bottom > top + 4 && o.top < top + height - 4 ? [i] : []));
  if (!seen.length) return null;
  const from = Math.max(0, parseFloat(strip.stops[seen[0]!]!.at) - 1.5);
  const to = Math.min(100, parseFloat(strip.stops[seen[seen.length - 1]!]!.at) + 1.5);
  return { left: `${from.toFixed(2)}%`, width: `${(to - from).toFixed(2)}%` };
}

function placeWindow(el: HTMLElement | null, at: { left: string; width: string } | null): void {
  if (!el) return;
  el.hidden = at === null;
  if (at) {
    el.style.left = at.left;
    el.style.width = at.width;
  }
}

/** Scroll the list just far enough to show this stop. */
function revealStop(card: Element | null, index: number): void {
  const list = card?.querySelector<HTMLElement>('.route__list');
  const row = list?.querySelector<HTMLElement>(`[data-stop="${index}"]`);
  if (!list || !row) return;
  const top = row.offsetTop - list.offsetTop;
  if (top < list.scrollTop) list.scrollTop = top - SEC_H;
  else if (top + STOP_H > list.scrollTop + list.clientHeight) list.scrollTop = top + STOP_H - list.clientHeight;
}

function stopTitle(s: Readonly<State>, stop: RouteStop): string {
  const lead = stop.bridges[0]!;
  const located = lead.lat != null && lead.lng != null;
  const selected = stop.bridges.some((b) => b.id === s.selectedId);
  return [
    ...stop.bridges.map(
      (b) =>
        `${join([b.id, b.type], ' — ')}\n${b.name} · ` +
        `${geometryCaption(b) ?? 'Geometry not recorded'} · ${standingLabel(inspectionStanding(b))}`,
    ),
    !located
      ? 'Click to open the record — this one has no position to show on the map'
      : selected && s.mapView !== null
        ? 'Click again to open the record'
        : 'Click to zoom the map to it',
  ].join('\n');
}

function strip(s: Readonly<State>, road: RoadCard): HTMLElement {
  const route = stretchShown(s, road);
  const windowKey = `${road.key}@${route.from}`;
  const seen = scrolled.get(windowKey);
  const offsets = stopOffsets(route);
  const fullHeight = (offsets[offsets.length - 1]?.bottom ?? 0) + LIST_PAD;
  const inView = listWindow(route, seen?.top ?? 0, seen?.height ?? Math.min(fullHeight, LIST_MAX));
  const isOn = (stop: RouteStop) => stop.bridges.some((b) => b.id === s.selectedId);

  /* The same two-step as a pin on the map above, and shared with it: the strip
     and the map are on one screen, so clicking a structure here shows it up
     there rather than leaving the screen. The record is the second click. */
  const pick = (b: BridgeView, index: number, from: Element) => {
    const card = from.closest('.route');
    selectOnMap(b, s);
    requestAnimationFrame(() => revealStop(card, index));
  };

  const row = (stop: RouteStop, index: number) => {
    const lead = stop.bridges[0]!;
    const twin = stop.bridges[1];
    const size = dotSize(stop);
    return h(
      'div',
      {
        class: ['route__stop', isOn(stop) && 'route__stop--on'],
        'data-stop': String(index),
      },
      h(
        'button',
        {
          type: 'button',
          class: 'route__go',
          title: stopTitle(s, stop),
          onclick: (e: Event) => pick(lead, index, e.currentTarget as Element),
        },
        h('span', { class: 'route__km', text: stationOf(lead) ?? NOT_RECORDED }),
        h(
          'span',
          { class: 'route__rail' },
          h('span', {
            class: ['route__node', twin && 'route__node--twin', isOn(stop) && 'route__node--on'],
            style: { width: `${size}px`, height: `${size}px` },
          }),
        ),
        h('span', { class: 'route__label', text: shortName(lead) }),
      ),
      twin
        ? h('button', {
            type: 'button',
            class: ['route__twin', twin.id === s.selectedId && 'route__twin--on'],
            title: `${twin.id} — ${twin.name}`,
            text: '+ parallel',
            onclick: (e: Event) => pick(twin, index, e.currentTarget as Element),
          })
        : null,
      h('span', {
        class: 'route__len',
        text: lead.length != null ? `${lead.length.toFixed(1)} m` : NOT_RECORDED,
      }),
    );
  };

  let index = 0;
  const list = route.sections.length
    ? route.sections.flatMap((sec) => [
        h(
          'div',
          { class: 'route__sec' },
          h('span', { class: 'route__rail' }, h('span', { class: 'route__node route__node--sec' })),
          h('b', { text: sec.label, title: active().groupings[0]?.label }),
          h('span', {
            class: 'route__sec-meta',
            text: `${kmSpan(sec.stops[0]!.bridges[0]!.km, sec.stops[sec.stops.length - 1]!.bridges[0]!.km)} · ${sec.count}`,
          }),
        ),
        ...sec.stops.map((stop) => row(stop, index++)),
      ])
    : route.stops.map((stop) => row(stop, index++));

  return card(
    { class: 'route' },
    h(
      'div',
      { class: 'route__head' },
      /* The code where the register has one, and nothing at all where it does
         not — the name beside it is always there and carries the card. */
      route.route ? h('span', { class: 'route__code', text: route.route }) : null,
      h('span', { class: 'route__name', text: route.roadName }),
      h('span', { class: 'spacer' }),
      h('span', {
        class: 'route__meta',
        text:
          `${route.count} structure${route.count === 1 ? '' : 's'} · ${route.totalLength} m deck` +
          // With a switch, the switch says which stretch this is.
          (road.stretches.length > 1 ? '' : ` · ${kmSpan(route.from, route.to)}`),
      }),
    ),

    road.stretches.length > 1
      ? h(
          'div',
          { class: 'route__stretches', role: 'group', 'aria-label': 'Stretch' },
          ...road.stretches.map((st) =>
            h('button', {
              type: 'button',
              class: 'route__stretch',
              'aria-pressed': st === route ? 'true' : 'false',
              text: kmSpan(st.from, st.to),
              onclick: () => actions.pickRouteStretch(road.key, st.from),
            }),
          ),
        )
      : null,

    h(
      'div',
      { class: 'route__strip' },
      ...route.sections.map((sec) =>
        h(
          'div',
          {
            class: 'route__band',
            style: { left: sec.from, width: `calc(${sec.to} - ${sec.from})` },
          },
          h('span', { text: sec.label }),
        ),
      ),
      h('div', { class: 'route__axis' }),
      h('div', {
        class: 'route__window',
        hidden: inView === null,
        style: inView ? { left: inView.left, width: inView.width } : undefined,
      }),
      ...route.stops.map((stop, i) => {
        const size = dotSize(stop);
        return h('button', {
          type: 'button',
          class: ['route__dot', stop.bridges.length > 1 && 'route__dot--twin', isOn(stop) && 'route__dot--on'],
          /* The list is the way through by keyboard; these are a mouse shortcut
             onto the same rows. */
          tabindex: '-1',
          'aria-hidden': 'true',
          title: stopTitle(s, stop),
          style: { left: stop.at, width: `${size}px`, height: `${size}px`, margin: `-${size / 2}px 0 0 -${size / 2}px` },
          onclick: (e: Event) => pick(stop.bridges[0]!, i, e.currentTarget as Element),
        });
      }),
      ...route.ticks.map((t) => h('span', { class: 'route__tick', style: { left: t.at }, text: String(t.km) })),
    ),

    h(
      'div',
      {
        class: 'route__list',
        onscroll: (e: Event) => {
          const el = e.currentTarget as HTMLElement;
          const at = { top: el.scrollTop, height: el.clientHeight };
          scrolled.set(windowKey, at);
          placeWindow(el.closest('.route')?.querySelector<HTMLElement>('.route__window') ?? null, listWindow(route, at.top, at.height));
        },
      },
      ...list,
    ),
  );
}

/**
 * Count and total deck length per district.
 *
 * Lives here rather than on the overview: it answers "where is the network",
 * which is this screen's question, and the overview's headline strip already
 * carries the whole-register totals.
 */
function districtCard(list: BridgeView[]): HTMLElement {
  const sheets = withInventorySheet(list);

  return card(
    { clip: true },
    cardHead('Bridges by district', 'Count and total structure length'),
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { text: 'District' }),
          h('th', { class: 'r', text: 'Bridges' }),
          h('th', { style: 'width:42%', text: 'Share of register' }),
          h('th', { class: 'r', text: 'Total length' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...districtRollups(list).map((d) =>
          h(
            'tr',
            {},
            h('td', { text: d.name }),
            h('td', { class: 'r num', style: 'color:var(--txt2)', text: String(d.count) }),
            h('td', {}, meter(d.share, 'var(--accent-solid)', 8)),
            h('td', { class: 'r num', text: `${d.totalLength} m` }),
          ),
        ),
      ),
    ),
    h('div', {
      class: 'note',
      /* Flush against the last row of the table above it: the card's gutter comes
         from the stylesheet, only the margin the table makes unnecessary is
         dropped here. */
      style: 'margin:0',
      /* THE DEPARTMENT'S OWN NAME AND THE DEPARTMENT'S OWN PROVENANCE. Both
         were hard-coded to DoR, so a DPWH screen said its records carried "a
         full DoR inventory sheet" and that they were "transcribed from as-built
         drawings" — one sentence, two claims, neither true of that register. */
      text: sheets.length
        ? `${sheets.length} structure${sheets.length === 1 ? '' : 's'} in the register ` +
          `carries a full ${agency().departmentShort} inventory sheet.`
        : `No structure in the register carries a full ${agency().departmentShort} ` +
          `inventory sheet yet — ${lowerFirst(active().registerProvenanceShort)}.`,
    }),
  );
}

/**
 * What is on the map and what is not, listed rather than counted.
 *
 * Only shown while something is missing a position. A record with no coordinates
 * is absent from the map entirely, and absence is exactly the thing a map cannot
 * report about itself — so it is reported here, by id, next to the map that is
 * not showing it.
 */
function unlocatedCard(list: BridgeView[]): HTMLElement | null {
  const missing = list.filter((b) => b.lat == null || b.lng == null);
  if (missing.length === 0) return null;

  return card(
    {},
    cardHead('Not on the map', 'Position not recorded'),
    h(
      'div',
      { class: 'rows' },
      h('div', {
        style: 'font-size:12.5px;color:var(--txt2);line-height:1.6',
        text:
          `${missing.length} of ${list.length} record${missing.length === 1 ? '' : 's'} ` +
          'carry no coordinates, so the map cannot show them. They are on the strips above ' +
          'by chainage, which every record has.',
      }),
      h(
        'div',
        { style: 'display:flex;gap:8px;flex-wrap:wrap;margin-top:12px' },
        ...missing.map((b) => tag(`${b.id} position not recorded`, 1)),
      ),
    ),
  );
}

/**
 * How many route strips the screen will draw.
 *
 * One per road, and DoR's network is 21 highways and 208 feeder roads — 229
 * strips is not a screen anybody reads, and it was the largest remaining cost
 * here at scale. The busiest roads first, since a strip's value is showing
 * several structures in relation to each other; a road with one bridge on it
 * says nothing a table does not.
 */
const MAX_STRIPS = 12;

/**
 * What the Map tab shows when there is no connection.
 *
 * THE MAP IS ONLINE ONLY, AND THIS IS WHERE THAT IS SAID. Tiles come from
 * OpenStreetMap and OpenTopoMap over the network; there is no bundled tile set
 * and no district download, because bulk-fetching either provider's tiles is
 * against the terms this application uses them under. So the honest answer
 * offline is a refusal with a reason, not an empty grey pane and not a map of
 * whatever happens to be left in the cache.
 *
 * What it offers instead is the thing that DOES work offline, which is the whole
 * point of the field shell: the register and every record in it, from this
 * device's own copy. A refusal that only says no is a dead end.
 *
 * The route strips are not drawn either. They need no tiles — they are chainage
 * on an axis — but they are a desk reading of the network - "which bridges are on
 * this road, in what order" - and on the field shell the Bridges tab already
 * answers "which bridge" better than a strip can at 360 px.
 */
function mapOffline(reason: string): HTMLElement {
  return card(
    { pad: true, class: 'nmap-off' },
    cardHead('Map unavailable'),
    h('p', { class: 'nmap-off__why', text: reason }),
    h('p', {
      class: 'nmap-off__note',
      text:
        'Map tiles are fetched from OpenStreetMap and OpenTopoMap and are not stored on this '
        + 'device. Bridges and their records do work offline, from this device’s own copy of the '
        + 'register.',
    }),
    button('Go to Bridges', {
      primary: true,
      block: true,
      tall: true,
      iconPath: ICONS.list,
      title: 'The register, which works without a connection',
      onClick: () => actions.go('list'),
    }),
  );
}

export function networkScreen(s: Readonly<State>): HTMLElement {
  /* The field shell's map refuses rather than degrades, and it refuses for the
     same reason its tab is dimmed — one predicate, named once, so the tab and the
     panel cannot disagree about whether there is a connection. */
  if (s.shell === 'field') {
    const reason = mapRefusal(s);
    if (reason) return h('div', { class: 'stack' }, mapOffline(reason));
  }

  const list = allBridges();
  const allRoads = roadCards(routeStrips(list));
  const roads = roadsShown(allRoads);

  /* Two halves when docked in the record: the map fills the top edge to edge,
     and everything else scrolls in the bottom. See `.netsplit` in app.css. */
  return h(
    'div',
    { class: 'netsplit' },
    nepalMapCard(s, list),
    h(
      'div',
      { class: 'netsplit__rest stack' },
      ...roads.map((road) => strip(s, road)),

      allRoads.length > roads.length
        ? h('div', {
            class: 'note',
            text: `${roads.length} busiest roads of ${allRoads.length} shown — the register screen lists every structure.`,
          })
        : null,

      districtCard(list),

      unlocatedCard(list),
    ),
  );
}

/**
 * The network, as the desk start page's second panel.
 *
 * The same three things the screen draws — the map, the route strips and the
 * district rollup — side by side rather than stacked: the map takes the width
 * and the strips and the rollup share a column beside it, so the panel sits
 * under the register without pushing it off the first screenful.
 */
export function networkPanel(s: Readonly<State>): HTMLElement {
  const list = allBridges();
  const allRoads = roadCards(routeStrips(list));
  const roads = roadsShown(allRoads);

  return panel(
    {
      title: 'Network',
      meta: `${allRoads.length} ${allRoads.length === 1 ? 'route' : 'routes'} · ${districtRollups(list).length} ${vocab().areaPlural.toLowerCase()}`,
      class: 'home-net',
    },
    h(
      'div',
      { class: 'home-net__split' },
      h('div', { class: 'home-net__map' }, nepalMapCard(s, list)),
      h(
        'div',
        { class: 'home-net__side stack' },
        ...roads.map((road) => strip(s, road)),
        allRoads.length > roads.length
          ? h('div', {
              class: 'note',
              text: `${roads.length} busiest roads of ${allRoads.length} shown — the register above lists every structure.`,
            })
          : null,
        districtCard(list),
        screeningCard(list),
        unlocatedCard(list),
      ),
    ),
  );
}

/**
 * Flood, scour and seismic screening, across the register — in the start
 * page's Network panel, beside the district rollup, since it is a reading taken
 * across the network rather than of any one structure.
 *
 * Two halves, and the second is the point. The distribution says how exposed the
 * network looks; the gap list says how much of that is a real finding and how
 * much is simply a column nobody has filled in. On today's five records the
 * larger number by far is "not screened", and a panel that showed only the
 * distribution would let that read as a clean bill of health.
 *
 * Every count is a link into the register the same way the other cards are: a
 * rollup nobody can drill into is a poster, not a tool.
 */
function screeningCard(list: BridgeView[]): HTMLElement {
  const counts = bandCounts(list);
  const gaps = screeningGaps(list);
  const worst = Math.max(1, ...BANDS.map((band) => counts[band]));

  return card(
    {},
    cardHead('Flood, scour and seismic screening', `Worst of the three per structure — ${list.length} records`),
    h(
      'div',
      { class: 'rows' },
      h(
        'div',
        {},
        ...BANDS.map((band) =>
          barRow(
            bandLabel(band),
            `${Math.round((counts[band] / worst) * 100)}%`,
            String(counts[band]),
          ),
        ),
      ),
      gaps.length
        ? h(
            'div',
            {},
            h('div', {
              class: 'note',
              style: 'margin-bottom:2px',
              text: 'What would unblock the unscreened ones, most useful first:',
            }),
            ...gaps.map((gap) => fact(gap.field, `${gap.count} structure${gap.count === 1 ? '' : 's'}`)),
          )
        : null,
      h('div', {
        class: 'note',
        text:
          '"Not screened" is not "fine" — it means the register does not hold what the screen needs. ' +
          'A screening aid over inventory fields, not a hydraulic, scour or seismic assessment.',
      }),
    ),
  );
}

/**
 * Lower-case the first letter, unless it starts something that is a name.
 *
 * The provenance clause is written to stand alone under a headline figure
 * ("All geometry from as-built GA drawings"), and here it is the tail of a
 * sentence. Left as-is it reads as a new sentence starting mid-clause; blindly
 * lower-cased it would write "dPWH" the first time a department puts its own
 * initials in that line. So: only where the second character is not also upper
 * case, which is the cheap test for an acronym.
 */
function lowerFirst(text: string): string {
  if (text.length < 2 || text[1] !== text[1]!.toLowerCase()) return text;
  return text[0]!.toLowerCase() + text.slice(1);
}
